import { useState, useCallback } from 'react';
import { Plane, Calendar, FileText, Edit3 } from 'lucide-react';
import { CompactTripCard } from '../components/journey/CompactTripCard';
import { TripDetailView } from '../components/journey/TripDetailView';
import PDFUploader from '../components/PDFUploader';
import ManualTripCreator from '../components/ManualTripCreator';
import { colors, radii, shadows, typography, fontWeights, spacing } from '../styles/designTokens';

// ── Sub-component: loading spinner ────────────────────────────────────────────
const LoadingView = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    background: 'linear-gradient(135deg, #dc2626 0%, #ffffff 50%, #1e40af 100%)'
  }}>
    <div style={{
      background: colors.surface,
      padding: '40px',
      borderRadius: radii.card,
      boxShadow: shadows.card,
      textAlign: 'center'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: `4px solid ${colors.divider}`,
        borderTop: `4px solid ${colors.urgent}`,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
      }} />
      <p style={{ color: colors.textSecondary, fontSize: typography.subheading.fontSize }}>Loading your trips...</p>
    </div>
  </div>
);

// ── Sub-component: error view ─────────────────────────────────────────────────
const ErrorView = ({ error, refetch }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '60vh',
    background: 'linear-gradient(135deg, #dc2626 0%, #ffffff 50%, #1e40af 100%)'
  }}>
    <div style={{
      background: colors.surface,
      padding: '40px',
      borderRadius: radii.card,
      boxShadow: shadows.card,
      textAlign: 'center',
      maxWidth: '500px'
    }}>
      <p style={{ color: colors.urgent, fontSize: typography.subheading.fontSize, marginBottom: '20px' }}>
        Error loading trips: {error.message}
      </p>
      <button
        onClick={() => refetch()}
        style={{
          padding: '12px 24px',
          background: 'linear-gradient(135deg, #dc2626 0%, #1e40af 100%)',
          color: colors.cardHeaderText,
          border: 'none',
          borderRadius: radii.inner,
          fontSize: typography.subheading.fontSize,
          fontWeight: fontWeights.semibold,
          cursor: 'pointer'
        }}
      >
        Retry
      </button>
    </div>
  </div>
);

// ── Sub-component: welcome / empty-state message ──────────────────────────────
const WelcomeMessage = ({ trips, showArchived }) => (
  <div style={{
    background: colors.surface,
    borderRadius: radii.card,
    padding: '40px',
    textAlign: 'center',
    boxShadow: shadows.card,
    marginBottom: '20px'
  }}>
    <Plane size={48} style={{ color: colors.textMuted, marginBottom: spacing.sectionGap }} />
    <h3 style={{
      fontSize: '24px',
      fontWeight: fontWeights.bold,
      color: colors.textPrimary,
      margin: '0 0 8px 0'
    }}>
      {trips.length === 0 ? 'Welcome to TravelFlow!' : showArchived ? 'No Past Trips' : 'No Upcoming Trips'}
    </h3>
    <p style={{
      color: colors.textSecondary,
      fontSize: typography.subheading.fontSize,
      margin: '0',
      lineHeight: '1.5'
    }}>
      {trips.length === 0 
        ? 'Create your first trip manually or upload a travel confirmation PDF to get started.'
        : showArchived 
          ? 'You don\'t have any past trips yet. Create a new trip below!'
          : 'You don\'t have any upcoming trips. Create a new trip below!'
      }
    </p>
  </div>
);

// ── Sub-component: creation mode toggle ───────────────────────────────────────
const CreationModeToggle = ({ creationMode, setCreationMode }) => (
  <div style={{
    background: colors.surface,
    borderRadius: radii.card,
    padding: '20px',
    boxShadow: shadows.card,
    marginBottom: '20px'
  }}>
    <p style={{
      color: colors.textSecondary,
      fontSize: typography.body.fontSize,
      margin: '0 0 16px 0'
    }}>
      Choose how you'd like to create your first trip
    </p>

    <div style={{
      display: 'flex',
      gap: spacing.cardGap,
      background: colors.surfaceAlt,
      padding: '4px',
      borderRadius: radii.inner,
      width: 'fit-content'
    }}>
      <button
        onClick={() => setCreationMode('pdf')}
        style={{
          padding: '10px 20px',
          fontSize: typography.body.fontSize,
          fontWeight: fontWeights.medium,
          color: creationMode === 'pdf' ? 'white' : colors.textSecondary,
          background: creationMode === 'pdf' ? colors.info : 'transparent',
          border: 'none',
          borderRadius: radii.inner,
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.elementGap
        }}
      >
        <FileText size={16} />
        Upload PDF
      </button>
      
      <button
        onClick={() => setCreationMode('manual')}
        style={{
          padding: '10px 20px',
          fontSize: typography.body.fontSize,
          fontWeight: fontWeights.medium,
          color: creationMode === 'manual' ? 'white' : colors.textSecondary,
          background: creationMode === 'manual' ? colors.info : 'transparent',
          border: 'none',
          borderRadius: radii.inner,
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.elementGap
        }}
      >
        <Edit3 size={16} />
        Manual Entry
      </button>
    </div>
  </div>
);

// ── Sub-component: PDF upload section ─────────────────────────────────────────
const PDFUploadSection = ({ onTripCreated, authToken, user }) => (
  <div style={{
    background: colors.surface,
    borderRadius: radii.card,
    padding: '40px',
    boxShadow: shadows.card
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: spacing.cardGap,
      marginBottom: '20px'
    }}>
      <FileText size={24} style={{ color: colors.urgent }} />
      <h3 style={{
        fontSize: typography.heading.fontSize,
        fontWeight: fontWeights.semibold,
        color: colors.textPrimary,
        margin: '0'
      }}>
        Create Trip from PDF
      </h3>
    </div>
    
    <p style={{
      color: colors.textSecondary,
      fontSize: typography.subheading.fontSize,
      margin: '0 0 20px 0',
      lineHeight: '1.5'
    }}>
      Upload your travel confirmation PDF to automatically extract flight details, hotel information, and car rental details.
    </p>
    
    <PDFUploader onTripCreated={onTripCreated} authToken={authToken} user={user} />
  </div>
);

function TripsPage({ trips, filteredTrips, showArchived, setShowArchived, isLoading, error, refetch, userAttributes, onTripDeleted, onTripUpdated, onTripCreated, authToken, user }) {
  const [creationMode, setCreationMode] = useState('pdf'); // 'manual' or 'pdf'
  const [selectedTripId, setSelectedTripId] = useState(null);

  // Navigation handler: CompactTripCard → TripCardFull (Req 9.2)
  const handleTripSelect = useCallback((tripId) => {
    setSelectedTripId(tripId);
  }, []);

  // Back handler: TripCardFull → trips list
  const handleBack = useCallback(() => {
    setSelectedTripId(null);
  }, []);

  if (isLoading) {
    return <LoadingView />;
  }

  if (error) {
    return <ErrorView error={error} refetch={refetch} />;
  }

  // If a trip is selected, render the TripDetailView (wires all data sources)
  if (selectedTripId) {
    const selectedTrip = trips.find((t) => t.tripId === selectedTripId);

    // If the trip was deleted or not found, go back to the list
    if (!selectedTrip) {
      setSelectedTripId(null);
      return null;
    }

    return (
      <div style={{ padding: '20px' }}>
        <TripDetailView
          trip={selectedTrip}
          userProfile={userAttributes}
          onBack={handleBack}
          onTripDeleted={(tripId) => {
            onTripDeleted(tripId);
            setSelectedTripId(null);
          }}
          onTripUpdated={onTripUpdated}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: fontWeights.semibold,
          color: 'white',
          margin: '0'
        }}>
          {showArchived ? 'Past Trips' : 'Upcoming Trips'}
        </h2>
        
        {trips.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => {
                // Toggle between upcoming (false) and past (true)
                setShowArchived(!showArchived);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.elementGap,
                padding: '10px 16px',
                background: showArchived ? 'linear-gradient(135deg, #1e40af 0%, #dc2626 100%)' : 'linear-gradient(135deg, #dc2626 0%, #1e40af 100%)',
                color: 'white',
                border: 'none',
                borderRadius: radii.inner,
                fontSize: typography.body.fontSize,
                fontWeight: fontWeights.medium,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <Calendar size={16} />
              {showArchived ? 'Show Upcoming' : 'Show Past Trips'}
            </button>
          </div>
        )}
      </div>
      
      {filteredTrips.length === 0 ? (
        <div>
          <WelcomeMessage trips={trips} showArchived={showArchived} />
          <CreationModeToggle creationMode={creationMode} setCreationMode={setCreationMode} />

          {creationMode === 'manual' && (
            <ManualTripCreator 
              onTripCreated={onTripCreated} 
              user={user} 
            />
          )}

          {creationMode === 'pdf' && (
            <PDFUploadSection onTripCreated={onTripCreated} authToken={authToken} user={user} />
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredTrips.map((trip) => (
            <CompactTripCard
              key={trip.tripId}
              trip={trip}
              flightStatuses={{}}
              departureCalculations={{}}
              onSelect={handleTripSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TripsPage;
